import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import './SearchArea.css';
import MultiSelectWithCheckboxes from '../../common/MultiSelectWithCheckboxes/MultiSelectWithCheckboxes';
import { WeeklyDatePicker } from '../../common/WeeklyDatePicker/WeeklyDatePicker';
import { FreeDatePicker } from '../../common/DatePicker/FreeDatePicker';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { WeeklyMaster } from './SearchArea';
import type { OptionsBase } from '../../../state/master';
import { useUserOptions } from '../../../state/master';
//import TaskMembersInput from '../../common/TaskMembersInput/TaskMembersInput';
import CommonSelect, { type Option as CSOption } from '../../common/commonSelect/CommonSelect';
import CheckboxDropdownSelect, {
  type CDSOption
} from '../../common/commonSelect/CheckboxDropdownSelect';
import CheckboxDropdownSelectWithAll from '../../common/commonSelect/CheckboxDropdownSelectWithAll';
import { buildPreferredTeamCandidates, useDepartmentUserOptions } from '../../../hooks/useDepartmentUserOptions';
import { useUserProfile } from '../../../context/UserProfileContext';
import { pickInitialTeamValue } from '../../../lib/initialTeamSelection';
import { USER_REGISTERED_EVENT } from '../../../lib/userRegistrationEvent';
import {
  CLASSIFICATION_MARKET_TARGET_ATTR_NAME,
  FIXED_ATTR_NAME_PROJECT,
  FIXED_ATTR_NAME_POWERTRAIN,
  FIXED_ATTR_NAME_SHIMUKE,
  FIXED_ATTR_NAME_LOT,
} from '../../../constants';
import {
  mergeCoAuthorOptionsByLabel,
  type CoAuthorOption,
  useCoAuthorRespondentOptions,
} from '../../../hooks/useCoAuthorRespondentOptions';
import {
  isCommonDisplayManagedAttr,
  isFreeInputManagedAttr,
  isManagedAttrVisible,
  normalizeManagedAttrNameKey,
  selectManagedAttrsForScope,
} from '../../common/utils/SearchArea.utils';
import {
  buildWeeklyReportPublicScopeDisplayMap,
  DEFAULT_PUBLIC_SCOPE_DISPLAY,
} from '../../../lib/weeklyReportPublicScopeDisplay';


/* =========================================================
 * 型・定数
 * ======================================================= */

interface DynamicFieldItem {
  id: string;
  type: string; // 表示名 = attr_name or 固定名
  value: any;
  label?: string;
  attr_id?: number;
  options?: { value: string; label: string }[];
  divisionContext?: string;
  isRemovable: boolean;
  isRequired?: boolean;
}

type AttrFromMaster = {
  attr_id: number;
  department_id: number;
  team_id: number;
  attr_name: string;
  label?: string;
  attr_required?: number | string;
  input_required?: number | string | boolean;
  attr_type: number | string; // 1:text … 11:select, 12:checkbox, 13:datalist
  multiple_select?: number | string | boolean; // 0 or 1
  order_number?: number | string;
  status?: string | number | boolean;
  options?: Array<{ value?: string | number; label?: string; option_id?: number; id?: number }> | null;
};

type Suggestion = { id: number; label: string };
type Tab = 'new' | 'overwrite' | 'sectionName' | null;

interface Props {
  activeTab: Tab;
  setActiveTab: (t: 'new' | 'overwrite') => void;
  division?: string;
  /**
   * 親 SearchArea で解決済みの department_id。
   * 追加課などで master.departments の label/value 解決に失敗しても、
   * ATTRIBUTE_MANAGE の課共通属性（team_id=0）を確実に拾うために優先使用する。
   */
  departmentId?: number | null;
  setDivision?: React.Dispatch<React.SetStateAction<string>>;
  team?: string;
  setTeam?: (value: string) => void;
  showPastTeams: boolean;
  setShowPastTeams: React.Dispatch<React.SetStateAction<boolean>>;
  reporter?: string;
  setReporter?: (value: string) => void;
  reportType?: string;
  setReportType?: (v: string) => void;
  startDate?: string;
  setStartDate?: (value: string) => void;
  endDate?: string;
  setEndDate?: (value: string) => void;
  approver3: string;
  setApprover3: (v: string) => void;
  approverAM: string;
  setApproverAM: (v: string) => void;
  shikimuke: string;
  setShikimuke: (v: string) => void;
  lot: string;
  setLot: (v: string) => void;
  enlightSyncEnabled: boolean;
  validationErrors?: { reporter?: string; startDate?: string; endDate?: string; title?: string; body?: string };
  onFieldsSnapshot?: (fields: any[]) => void;
  /** { freeWord_1: '…', freeWord_1_0: '…' ... } を通知（_0 も含む） */
  onFreeWordsChange?: (freeWords: Record<string, string>) => void;
  /** バックエンド保存用: [{ attr_id, attr_name, attr_value }] を通知 */
  onAttributesChange?: (attrs: Array<{ attr_id: number; attr_name: string; attr_value: string }>) => void;
  master: WeeklyMaster | null;
  divisionKind?: 1 | 2 | 3;
  divisionLabel?: string;
  onHydrated?: () => void;

  // NEWの前回入力値（動的項目）
  initialNewDraftFields?: Array<{
    id: string;
    type: string;
    label?: string;
    attr_id?: number;
    value: any;
    isRemovable?: boolean;
  }> | null;

  // 課題メンバーを親で保持（タブ切り替えで消えないようにする）
  taskMembers?: any[] | undefined;
  setTaskMembers?: React.Dispatch<React.SetStateAction<any[] | undefined>>;
  initialNewDraftFreeWords?: Record<string, string> | null;
  /** ログインユーザーID（respondents候補の取得に使用） */
  loginId?: string;
}

/** 固定ラベル → CSS クラス名 */
const clsMap: Record<string, string> = {
  チーム: 'team',
  記入者: 'reporter',
  連名者: 'task-members',
  '承認-3': 'approver3',
  承認AM: 'approver-am',
  [CLASSIFICATION_MARKET_TARGET_ATTR_NAME]: 'report-type',
  開始終了日付: 'date-range',
  [FIXED_ATTR_NAME_PROJECT]: 'project',
  [FIXED_ATTR_NAME_POWERTRAIN]: 'powertrain',
  [FIXED_ATTR_NAME_SHIMUKE]: 'destination',
  [FIXED_ATTR_NAME_LOT]: 'lot'
};

/** 真に固定の項目（“属性”ではない） */
const FIXED_BASE_NAMES = new Set<string>(['チーム', '記入者', '連名者', '承認-3', '承認AM', '開始終了日付']);
const APPROVER3_DISPLAY_LABEL = '職制';
const APPROVER_AM_DISPLAY_LABEL = 'AM';

/* =========================================================
 * ユーティリティ
 * ======================================================= */

const norm = (s: any) => String(s ?? '').normalize('NFKC').trim();
const toOrderNumberOrMax = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

const sameName = (a?: string, b?: string) => norm(a) === norm(b);

const slugify = (s: string) => norm(s).toLowerCase().replace(/[^\w\u3040-\u30ff\u4e00-\u9faf-]+/g, '-');

const PROCESS_PLANT_ATTR_KEY = '工順生産拠点';

const normalizeLayoutAttrName = (name: string) => norm(name).replace(/[\s()（）]/g, '');

const isProcessPlantAttr = (name: string) => normalizeLayoutAttrName(name) === PROCESS_PLANT_ATTR_KEY;

const prioritizeProcessPlantAttr = (names: readonly string[]): string[] => {
  const processPlantNames = names.filter(isProcessPlantAttr);
  if (processPlantNames.length === 0) return [...names];
  return [...processPlantNames, ...names.filter((name) => !isProcessPlantAttr(name))];
};

const sameJson = (a: any, b: any) => {
  try {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  } catch {
    return a === b;
  }
};

const toAttrTypeCode = (t: number | string | undefined): number => {
  const s = norm(t);
  const map: Record<string, number> = {
    '1': 1,
    text: 1,
    '2': 2,
    textarea: 2,
    '3': 3,
    int: 3,
    integer: 3,
    '4': 4,
    decimal: 4,
    float: 4,
    '5': 5,
    date: 5,
    '6': 6,
    time: 6,
    '11': 11,
    select: 11,
    dropdown: 11,
    '12': 12,
    checkbox: 12,
    multiselect: 12,
    '13': 13,
    datalist: 13,
    combo: 13
  };
  if (s in map) return map[s];
  const n = Number(s);
  return Number.isFinite(n) ? n : 1;
};

const toMultiFlag = (v: number | string | boolean | undefined): 0 | 1 => {
  const s = norm(v).toLowerCase();
  return s === '1' || s === 'true' || s === 't' || s === 'yes' || s === 'y' || s === 'on' ? 1 : 0;
};

const isSelectType = (t?: number | string) => [11, 12, 13].includes(toAttrTypeCode(t));

const isFreeWordType = (t?: number | string) => !isSelectType(t);

const parseTeamFiscalYear = (raw: unknown): number | null => {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw !== 'string') return null;

  const s = raw.trim().toUpperCase();
  if (!s) return null;

  const fyMatch = s.match(/^FY(\d{2}|\d{4})$/);
  if (fyMatch) {
    const n = Number(fyMatch[1]);
    if (!Number.isFinite(n)) return null;
    return fyMatch[1].length === 2 ? 2000 + n : n;
  }

  if (/^\d{4}$/.test(s)) {
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  return null;
};

const isPastTeam = (teamFiscalYear: unknown): boolean => {
  const fy = parseTeamFiscalYear(teamFiscalYear);
  return fy != null;
};

const getTeamEndFiscalYear = (team: any): unknown =>
  team?.end_fiscal_year ?? team?.team_fiscal_year;

const toOption = (x: any): { value: string; label: string; groupLabel?: string } => {
  if (typeof x === 'string' || typeof x === 'number') {
    const s = norm(x);
    return { value: s, label: s };
  }

  const pickStr = (obj: any, key: string) => {
    const v = obj?.[key];
    if (typeof v === 'string' || typeof v === 'number') return norm(v);
    if (v && typeof v === 'object') {
      // {label,value,name} っぽい形も吸収
      return norm(v.label ?? v.value ?? v.name ?? '');
    }
    return '';
  };

  const groupLabel = norm(
    x?.groupLabel ??
      x?.team_classification_name ??
      x?.team_class_name ??
      x?.team_classification ??
      x?.class_name ??
      x?.classification_name ??
      ''
  );

  // まずは一般的な value/label/name/text を優先
  const value0 = norm(x?.value ?? x?.label ?? x?.name ?? x?.text ?? '');
  const label0 = norm(x?.label ?? x?.name ?? x?.text ?? x?.value ?? value0);
  if (value0 || label0) {
    const value = value0 || label0;
    const label = label0 || value0 || value;
    return groupLabel ? { value, label, groupLabel } : { value, label };
  }

  // ここから「姓・名」系の旧形式を吸収（名の拾い漏れ対策）
  const lastKeys = [
    'lastName', 'lastname', 'last_name', 'familyName', 'family_name', 'surname', 'surName',
    'sei', 'family', 'last', '姓', '氏', '苗字'
  ];
  const firstKeys = [
    'firstName', 'firstname', 'first_name', 'givenName', 'given_name', 'mei',
    'given', 'first', '名', '名前'
  ];

  const lastName = lastKeys.map((k) => pickStr(x, k)).find(Boolean) ?? '';
  const firstName = firstKeys.map((k) => pickStr(x, k)).find(Boolean) ?? '';

  const fullName = norm([lastName, firstName].filter(Boolean).join(' '));
  if (fullName) return groupLabel ? { value: fullName, label: fullName, groupLabel } : { value: fullName, label: fullName };

  const name2 = norm(pickStr(x, 'fullName') || pickStr(x, 'fullname') || pickStr(x, 'displayName') || pickStr(x, 'name'));
  if (name2) return groupLabel ? { value: name2, label: name2, groupLabel } : { value: name2, label: name2 };

  return { value: '', label: '' };
};

const normalizeOptions = (
  arr: any[] | undefined | null
): { value: string; label: string; groupLabel?: string }[] =>
  (arr ?? [])
    // status='N' や false を除外
    .filter((o: any) => String((o as any)?.status ?? '').toUpperCase() !== 'N' && (o as any)?.status !== false)
    .map(toOption);

const uniqueOptions = (opts: { value: string; label: string; groupLabel?: string }[]) => {
  const seen = new Set<string>();
  return (opts ?? []).filter((o) => {
    const k = `${o.value}__${o.label}__${norm(o.groupLabel ?? '')}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

// string / {value,label} / null から CommonSelect の現在選択値を生成（value/label 両方でマッチ）
const toSelected = (raw: any, opts: { value: string; label: string }[]): CSOption | null => {
  const list = uniqueOptions(opts);
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') {
    const v = norm((raw as any).value);
    const l = norm((raw as any).label);
    const hit = list.find((o) => norm(o.value) === v) || list.find((o) => norm(o.label) === l);
    const value = v || l;
    const label = l || v || value;
    return (hit as CSOption) ?? ({ value, label } as CSOption);
  }
  const s = norm(String(raw));
  const hit = list.find((o) => norm(o.value) === s) || list.find((o) => norm(o.label) === s);
  return (hit as CSOption) ?? ({ value: s, label: s } as CSOption);
};

const toSuggestions = (opts: { value: string; label: string }[]): Suggestion[] =>
  opts.map((o, i) => {
    const n = Number(o.value);
    return { id: Number.isFinite(n) ? n : i + 1, label: o.label };
  });

const uniqueNames = (arr: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of arr) {
    const k = String(s ?? '');
    if (!seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out;
};

/* ---------- select(複数)の選択値を options に合わせて正規化（IDだけ→ラベル補完/不正値除去） ---------- */
const normalizeSelectArrayValue = (raw: any): { value: string; label: string }[] => {
  const arr: any[] = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
    ? norm(raw)
        .split(',')
        .map((s) => norm(s))
        .filter(Boolean)
    : [];

  const out = arr
    .map((x) => toOption(x))
    .map((o) => ({ value: norm(o.value), label: norm(o.label) }))
    .filter((o) => o.value !== '' || o.label !== '');

  return uniqueOptions(out);
};

const sanitizeSelectArrayByOptions = (
  raw: any,
  options: { value: string; label: string }[]
): { value: string; label: string }[] => {
  const normalized = normalizeSelectArrayValue(raw);

  const optList = uniqueOptions(options ?? []);
  // options がまだ空（ロード中など）の場合は温存（消すと「一瞬消えて戻る」事故が起きる）
  if (optList.length === 0) return normalized;

  const byValue = new Map<string, { value: string; label: string }>();
  const byLabel = new Map<string, { value: string; label: string }>();
  optList.forEach((o) => {
    byValue.set(norm(o.value), o);
    byLabel.set(norm(o.label), o);
  });

  const out: { value: string; label: string }[] = [];
  normalized.forEach((it) => {
    const v = norm(it.value);
    const l = norm(it.label);

    const hit =
      byValue.get(v) ||
      byLabel.get(l) ||
      byValue.get(l) ||
      byLabel.get(v);

    if (!hit) return; // options に無い値（例: 91/164）は捨てる
    out.push({ value: norm(hit.value), label: norm(hit.label) });
  });

  return uniqueOptions(out);
};

/* ---------- 連名者（複数選択）の値正規化 ---------- */
/**
 * 連名者は Array<{value,label}> を基本とする
 * - 旧: TaskMembersInput のオブジェクト配列（lastName/firstName等）を吸収
 * - string/number も吸収
 */
const normalizeCoAuthorsValue = (raw: any): CoAuthorOption[] => {
  const arr = Array.isArray(raw) ? raw : [];

  const pickStr = (obj: any, key: string) => {
    const v = obj?.[key];
    if (typeof v === 'string' || typeof v === 'number') return norm(v);
    if (v && typeof v === 'object') {
      return norm(v.label ?? v.value ?? v.name ?? '');
    }
    return '';
  };

  const pickAny = (obj: any, keys: string[]) => keys.map((k) => pickStr(obj, k)).find(Boolean) ?? '';

  const out = arr
    .map((x: any): CoAuthorOption | null => {
      if (x == null) return null;

      if (typeof x === 'string' || typeof x === 'number') {
        const s = norm(x);
        return s ? { value: s, label: s } : null;
      }

      if (typeof x === 'object') {
        // { value, label } 形式
        const v = norm(x.value ?? '');
        const l = norm(x.label ?? '');
        if (v || l) {
          const value = v || l;
          const label = l || v || value;
          return { value, label };
        }

        // ★旧TaskMembersInput互換（姓・名のキー候補を増やす）
        const lastName = pickAny(x, [
          'lastName', 'lastname', 'last_name', 'familyName', 'family_name', 'surname', 'surName',
          'sei', 'family', 'last', '姓', '氏', '苗字'
        ]);
        const firstName = pickAny(x, [
          'firstName', 'firstname', 'first_name', 'givenName', 'given_name', 'mei',
          'given', 'first', '名', '名前'
        ]);

        const name = norm([lastName, firstName].filter(Boolean).join(' '));
        if (name) return { value: name, label: name };

        // さらに fallback
        const name2 = norm(
          pickAny(x, ['name', 'fullName', 'fullname', 'displayName', 'display_name', 'user_name', 'username'])
        );
        if (name2) return { value: name2, label: name2 };
      }

      return null;
    })
    .filter(Boolean) as CoAuthorOption[];

  return uniqueOptions(out);
};


/* ---------- 連名者：（旧:自由入力の幽霊値を除去/補完） ---------- */
/**
 * - 復元（localStorage/初期値/親同期）では options に無い値は消す
 * - ユーザーが Enter で追加した自由入力は残す（allowCustomValue=true のときのみ）
 * - 旧「姓だけ」→ 現「フルネーム」 / 旧「フルネーム」→ 現「姓だけ」は
 *   文字列として部分一致関係があり、かつ一意なら置換する（曖昧なら消す）
 */
const coAuthorKeyLoose = (s: string): string =>
  norm(s)
    .replace(/[ 　\t\r\n]+/g, '') // 空白（半角/全角/タブ/改行）
    .replace(/[・\u30fb]/g, '') // 中点
    .replace(/[-_]/g, ''); // 記号

const isDigitOnly = (s: string) => /^\d+$/.test(String(s ?? '').trim());

const sanitizeCoAuthorsByOptions = (
  raw: any,
  options: { value: string; label: string }[],
  cfg?: { allowCustomValue?: boolean }
): CoAuthorOption[] => {
  const allowCustomValue = !!cfg?.allowCustomValue;

  const normalized = normalizeCoAuthorsValue(raw);

  const optList = uniqueOptions((options ?? []).map(toOption));
  // options が空（ロード中）なら温存（消すとチラつき事故）
  if (optList.length === 0) return normalized;

  const byValue = new Map<string, CoAuthorOption>();
  const byLabel = new Map<string, CoAuthorOption>();

  // looseKey -> options(同じキーが複数あると曖昧なので配列で保持)
  const byLooseLabel = new Map<string, CoAuthorOption[]>();

  optList.forEach((o) => {
    const v = norm(o.value);
    const l = norm(o.label);

    if (v) byValue.set(v, o);
    if (l) byLabel.set(l, o);

    const lk = coAuthorKeyLoose(l);
    if (!lk) return;
    const arr = byLooseLabel.get(lk) ?? [];
    arr.push(o);
    byLooseLabel.set(lk, arr);
  });

  const pickUnique = (cands: CoAuthorOption[]): CoAuthorOption | null => {
    const uniq = uniqueOptions(cands);
    return uniq.length === 1 ? uniq[0] : null;
  };

  const resolveOne = (it: CoAuthorOption): CoAuthorOption | null => {
    const v = norm(it.value);
    const l = norm(it.label);

    // 1) 完全一致（value/label）
    const hit =
      byValue.get(v) ||
      byLabel.get(l) ||
      byValue.get(l) ||
      byLabel.get(v);
    if (hit) return hit;

    const probe = coAuthorKeyLoose(l || v);
    // 1文字の推定は誤爆が多いので禁止（要件の「安全側」）
    if (!probe || probe.length < 2) {
      return allowCustomValue ? (v || l ? { value: v || l, label: l || v || (v || l) } : null) : null;
    }
    if (!allowCustomValue && isDigitOnly(probe)) return null;

    // 2) looseKey 完全一致（ただし一意のときだけ）
    const exactLoose = byLooseLabel.get(probe);
    if (exactLoose) {
      const u = pickUnique(exactLoose);
      if (u) return u;
    }

    // 3) 旧「姓だけ」→ 現「フルネーム」 (optionKey startsWith probe)
    //    例: probe=鈴木 / option=鈴木一郎  => 一意なら採用
    const longer = optList.filter((o) => coAuthorKeyLoose(o.label).startsWith(probe));
    if (longer.length > 0) {
      const u = pickUnique(longer);
      if (u) return u;
    }

    // 4) 旧「フルネーム」→ 現「姓だけ」 (probe startsWith optionKey)
    //    例: probe=山田太郎 / option=山田  => 最長一致が一意なら採用
    const shorter = optList.filter((o) => {
      const ok = coAuthorKeyLoose(o.label);
      return ok.length >= 2 && probe.startsWith(ok);
    });
    if (shorter.length > 0) {
      const maxLen = Math.max(...shorter.map((o) => coAuthorKeyLoose(o.label).length));
      const longest = shorter.filter((o) => coAuthorKeyLoose(o.label).length === maxLen);
      const u = pickUnique(longest);
      if (u) return u;
    }

    // 5) contains 一意（最後の保険）
    const contains = optList.filter((o) => coAuthorKeyLoose(o.label).includes(probe));
    if (contains.length > 0) {
      const u = pickUnique(contains);
      if (u) return u;
    }

    // 6) 一致しない値の扱い
    // 復元系では捨てる / ユーザー入力では残す
    if (!allowCustomValue) return null;

    const value = v || l;
    const label = l || v || value;
    return value || label ? { value, label } : null;
  };

  const out: CoAuthorOption[] = [];
  normalized.forEach((it) => {
    const r = resolveOne({ value: norm(it.value), label: norm(it.label) });
    if (!r) return;
    out.push({ value: norm(r.value), label: norm(r.label) });
  });

  return uniqueOptions(out);
};

/* ---------- 連名者：安定シグネチャ（順不同でも一致する） ---------- */
const coAuthorsSig = (raw: any): string => {
  const arr = normalizeCoAuthorsValue(raw)
    .map((o: CoAuthorOption) => ({ value: norm(o.value), label: norm(o.label) }))
    .filter((o: CoAuthorOption) => o.value !== '' || o.label !== '')
    .sort(
      (a: CoAuthorOption, b: CoAuthorOption) =>
        a.value.localeCompare(b.value, 'ja') ||
        a.label.localeCompare(b.label, 'ja')
    );

  try {
    return JSON.stringify(arr);
  } catch {
    return String(Date.now());
  }
};

/* ---------- 連名者：subset 判定（親の値が少なくても子を縮めない） ---------- */
const isCoAuthorsSubset = (smallRaw: any, bigRaw: any): boolean => {
  const small = normalizeCoAuthorsValue(smallRaw);
  const big = normalizeCoAuthorsValue(bigRaw);

  const keyOf = (o: CoAuthorOption) => {
    const v = norm(o.value);
    const l = norm(o.label);
    return v || l;
  };

  const bigSet = new Set(big.map(keyOf).filter(Boolean));
  return small.every((o: CoAuthorOption) => {
    const k = keyOf(o);
    return k ? bigSet.has(k) : false;
  });
};

/** 表示優先で値化（label → value → name） */
const pickValueLike = (v: any): string => {
  if (v == null) return '';
  if (Array.isArray(v)) {
    return v
      .map((x) => (typeof x === 'object' ? String(x.label ?? x.value ?? x.name ?? '') : String(x)))
      .map(norm)
      .filter(Boolean)
      .join(',');
  }
  if (typeof v === 'object') {
    return norm((v as any).label ?? (v as any).value ?? (v as any).name ?? JSON.stringify(v));
  }
  return norm(v);
};

const isBlankValue = (v: any): boolean => {
  if (v == null) return true;
  if (typeof v === 'string') return norm(v) === '';
  if (typeof v === 'number') return false; // 0 は値として扱う
  if (typeof v === 'boolean') return false;
  if (Array.isArray(v)) return v.length === 0 || v.every((x) => isBlankValue(x));
  if (typeof v === 'object') {
    const o: any = v;
    // date range
    if ('from' in o || 'to' in o) return norm(o.from ?? '') === '' && norm(o.to ?? '') === '';
    // select / datalist
    if ('value' in o || 'label' in o) return norm(o.value ?? '') === '' && norm(o.label ?? '') === '';
  }
  return false;
};

/* ---------- options整形：パワートレイン/ロットは「共通」を先頭に ---------- */
const COMMON_LABEL = '共通'; // 
const COMMON_FIRST_ATTRS = new Set<string>([FIXED_ATTR_NAME_POWERTRAIN, FIXED_ATTR_NAME_LOT]); //　ロット、一旦外しているなので空文字

const applyCommonFirstOption = (
  opts: { value: string; label: string }[],
  attrName?: string
): { value: string; label: string }[] => {
  const name = norm(attrName ?? '');
  const list = uniqueOptions(opts ?? []);

  if (!COMMON_FIRST_ATTRS.has(name)) return list;

  const isCommon = (o: { value: string; label: string }) =>
    norm(o.label) === COMMON_LABEL || norm(o.value) === COMMON_LABEL;

  const hasCommon = list.some(isCommon);

  // ロットは「共通」自体が無いケースがあるので注入（value=label でOK）
  const withCommon = hasCommon
    ? list
    : [{ value: COMMON_LABEL, label: COMMON_LABEL }, ...list];

  const common = withCommon.filter(isCommon);
  const rest = withCommon.filter((o) => !isCommon(o));

  return [...common, ...rest];
};

/** バケットから候補を抽出（仕向け/ロットは label を value として扱う） */
const optionsFromBuckets = (
  master: WeeklyMaster | null,
  attrName?: string,
  attrId?: number,
  departmentId?: number | null,
  teamId?: number | null
): { value: string; label: string }[] => {
  const buckets = (master as any)?.attributeBuckets as Array<{ name: string; items: any[] }> | undefined;
  if (!buckets) return [];

  const preferLabelAsValue = attrName === FIXED_ATTR_NAME_SHIMUKE || attrName === FIXED_ATTR_NAME_LOT;

  const make = (it: any, idx: number) => {
    const label = norm(it?.label ?? it?.name ?? it?.text ?? it?.value ?? `item-${idx + 1}`);
    const value = preferLabelAsValue ? label : (it?.option_id ?? it?.id ?? it?.value ?? label) + '';
    return { value: norm(value), label };
  };

  const filterByScope = (items: any[]) => {
    const tn = Number(teamId) || 0;
    let dn = Number(departmentId) || 0;
    const strictTeamScope = attrName === CLASSIFICATION_MARKET_TARGET_ATTR_NAME || attrName === FIXED_ATTR_NAME_PROJECT;

    const teamItems = (items ?? []).filter((it: any) => Number(it?.team_id ?? it?.teamId) === tn && tn > 0);
    if (teamItems.length) return teamItems;

    if (tn > 0) {
      const teams: any[] = Array.isArray((master as any)?.teams) ? (master as any).teams : [];
      const hitTeam = teams.find((t: any) => Number(t?.team_id ?? t?.id) === tn);
      const teamDeptId = Number(hitTeam?.department_id) || 0;
      if (teamDeptId) dn = teamDeptId;

      const deptItems = (items ?? []).filter(
        (it: any) =>
          Number(it?.team_id ?? it?.teamId) === 0 &&
          Number(it?.department_id ?? it?.departmentId) === dn &&
          dn > 0
      );
      if (deptItems.length) return deptItems;

      const companyItems = (items ?? []).filter(
        (it: any) => Number(it?.team_id ?? it?.teamId) === 0 && Number(it?.department_id ?? it?.departmentId) === 0
      );
      if (companyItems.length) return companyItems;

      return strictTeamScope ? [] : items ?? [];
    }

    const deptItemsNoTeam = (items ?? []).filter(
      (it: any) =>
        Number(it?.team_id ?? it?.teamId) === 0 &&
        Number(it?.department_id ?? it?.departmentId) === dn &&
        dn > 0
    );
    if (deptItemsNoTeam.length) return deptItemsNoTeam;

    const companyItemsNoTeam = (items ?? []).filter(
      (it: any) => Number(it?.team_id ?? it?.teamId) === 0 && Number(it?.department_id ?? it?.departmentId) === 0
    );
    if (companyItemsNoTeam.length) return companyItemsNoTeam;

    return items ?? [];
  };

  const collect = (items: any[]) => {
    const seen = new Set<string>();
    const out: { value: string; label: string }[] = [];
    filterByScope(items)
      .filter((it: any) => String((it as any)?.status ?? '').toUpperCase() !== 'N' && (it as any)?.status !== false)
      .forEach((it, i) => {
        const o = make(it, i);
        const k = `${o.value}__${o.label}`;
        if (!o.label || seen.has(k)) return;
        seen.add(k);
        out.push(o);
      });
    return out;
  };

  const itemsByAttrId =
    typeof attrId === 'number'
      ? buckets.flatMap((b) =>
          (b.items ?? []).filter((it: any) => Number(it?.attr_id ?? it?.attrId) === Number(attrId))
        )
      : [];
  const bucketByName = attrName ? buckets.find((b) => norm(b?.name) === norm(attrName)) : undefined;
  const itemsByName = bucketByName?.items ?? [];

  const combined = [...itemsByAttrId, ...itemsByName];
  if (combined.length > 0) {
    const out = collect(combined);
    return applyCommonFirstOption(out, attrName);
  }

  return [];
};


const sanitizeIntStrict = (raw: any) => {
  const s0 = String(raw ?? '');
  if (!s0) return '';
  let s = s0.replace(/[^\d-]/g, '');
  const neg = s.startsWith('-');
  s = s.replace(/-/g, '');
  return (neg ? '-' : '') + s;
};

const getDeptIdFromMaster = (master: WeeklyMaster | null, division?: string): number | null => {
  if (!master || !division) return null;

  const normalizeKey = (value: unknown): string => norm(value).toUpperCase();
  const key = normalizeKey(division);
  const depts: any[] = Array.isArray((master as any)?.departments)
    ? (master as any).departments
    : [];

  const pickDeptId = (dept: any): number | null => {
    const id = Number(
      dept?.department_id ??
        dept?.departmentId ??
        dept?.dept_id ??
        dept?.id
    );

    return Number.isFinite(id) && id > 0 ? id : null;
  };

  const hit = depts.find((d: any) => {
    const id = pickDeptId(d);
    const candidates = [
      d?.label,
      d?.value,
      d?.name,
      d?.department,
      d?.department_name,
      d?.departmentName,
      d?.dept_name,
      d?.deptName,
      id != null ? String(id) : '',
    ];

    return candidates.some((candidate) => normalizeKey(candidate) === key);
  });

  return pickDeptId(hit);
};

/* =========================================================
 * 課題メンバーのフォールバック永続化（props未提供でもタブ切替で消えないようにする）
 * ======================================================= */

/** 新規登録直後に「自分のチーム」を優先して選択し直す有効時間 */
const FORCE_PREFERRED_TEAM_WINDOW_MS = 15_000;
const TASK_MEMBERS_CACHE_PREFIX = 'weekly_report_task_members_v1';
const TASK_MEMBERS_CACHE_TTL_MS = 14 * 24 * 60 * 60 * 1000;
const TASK_MEMBERS_MAX_SELECTED = 10;
const TASK_MEMBERS_MAX_SELECTED_MESSAGE = `最大${TASK_MEMBERS_MAX_SELECTED}名まで選択できます`;

const limitTaskMembers = <T,>(members: T[]): T[] =>
  Array.isArray(members) ? members.slice(0, TASK_MEMBERS_MAX_SELECTED) : [];

const readTaskMembersCache = (key: string): any[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return limitTaskMembers(parsed);
    if (!parsed || typeof parsed !== 'object') {
      window.sessionStorage.removeItem(key);
      return [];
    }

    const savedAt = Number((parsed as any).savedAt);
    if (!Number.isFinite(savedAt) || Date.now() - savedAt > TASK_MEMBERS_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(key);
      return [];
    }

    return Array.isArray((parsed as any).members) ? limitTaskMembers((parsed as any).members) : [];
  } catch {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* no-op */
    }
    return [];
  }
};

const writeTaskMembersCache = (key: string, members: any[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      key,
      JSON.stringify({
        savedAt: Date.now(),
        members: Array.isArray(members) ? limitTaskMembers(members) : [],
      })
    );
  } catch {
    /* no-op */
  }
};


/* =========================================================
 * Select(複数)の軽量コンポーネント（検索付き）
 * ======================================================= */

const useOutsideClose = (ref: React.RefObject<HTMLElement | null>, onClose: () => void) => {
  useEffect(() => {
    const h = (e: MouseEvent | TouchEvent) => {
      const el = e.target as Node | null;
      if (ref.current && el && !ref.current.contains(el)) onClose();
    };
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('touchstart', h);
    };
  }, [ref, onClose]);
};


/* =========================================================
 * メイン
 * ======================================================= */

const SearchArea1st: React.FC<Props> = ({
  activeTab,
  division,
  departmentId,
  team,
  setTeam,
  showPastTeams,
  setShowPastTeams,
  reporter,
  setReporter,
  reportType,
  setReportType,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  approver3,
  setApprover3,
  approverAM,
  setApproverAM,
  shikimuke,
  setShikimuke,
  lot,
  setLot,
  enlightSyncEnabled,
  onFieldsSnapshot,
  onFreeWordsChange,
  onAttributesChange,
  master,
  onHydrated,
  initialNewDraftFields,
  taskMembers,
  setTaskMembers,
  initialNewDraftFreeWords,
  loginId
}) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const reportTypeScopeDisplayByOptionId = useMemo(
    () =>
      buildWeeklyReportPublicScopeDisplayMap({
        rows: master?.wrPublicSettings,
        departments: master?.departments,
        teams: master?.teams,
        maxVisible: 3,
      }),
    [master?.wrPublicSettings, master?.departments, master?.teams]
  );
  const datalistInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // 連名者：ユーザーが操作したら親同期による巻き戻しを止める
  const didTouchTaskMembersRef = useRef(false);
  // 連名者：親が追いつくまでの一時保護（直後の巻き戻し防止）
  const pendingTaskMembersSigRef = useRef<string>('');
  // 連名者：重い親同期・キャッシュ保存を少し遅らせる
  const taskMembersSaveTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (taskMembersSaveTimerRef.current != null) {
        window.clearTimeout(taskMembersSaveTimerRef.current);
      }
    };
  }, []);


  // select(複数/単一)：ユーザーが触ったら draft の自動再適用を止める
  const touchedSelectAttrNamesRef = useRef<Set<string>>(new Set());
  const markSelectTouched = (type: string) => {
    touchedSelectAttrNamesRef.current.add(norm(type));
  };


  const SUPPORTS_DATALIST_PICKER =
    typeof window !== 'undefined' && typeof (HTMLInputElement.prototype as any).showPicker === 'function';

  const SyncIcon = enlightSyncEnabled ? (
    <CheckCircleIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} color="success" />
  ) : (
    <CheckCircleOutlineIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} color="disabled" />
  );

  /* ---------- 課題メンバーのキャッシュKey ---------- */

  const taskMembersCacheKey = useMemo(() => {
    const d = norm(division ?? '');
    return `${TASK_MEMBERS_CACHE_PREFIX}:${d || 'global'}`;
  }, [division]);

  /* ---------- マスター派生 ---------- */

  const selectedDeptId = useMemo(() => {
    if (typeof departmentId === 'number' && Number.isFinite(departmentId) && departmentId > 0) {
      return departmentId;
    }

    return getDeptIdFromMaster(master, division);
  }, [departmentId, master, division]);

  const teamsForSelectedDept = useMemo(() => {
    const teams: any[] = Array.isArray((master as any)?.teams) ? (master as any).teams : [];
    return typeof selectedDeptId === 'number'
      ? teams.filter((t: any) => Number(t?.department_id ?? t?.dept_id ?? 0) === selectedDeptId)
      : teams;
  }, [master, selectedDeptId]);

  const teamOpts = useMemo(() => {
    const filtered = teamsForSelectedDept.filter((t: any) => {
      if (showPastTeams) return true;
      return !isPastTeam(getTeamEndFiscalYear(t));
    });
    const sorted = [...filtered].sort((a: any, b: any) => {
      const ao = toOrderNumberOrMax((a as any)?.order_number);
      const bo = toOrderNumberOrMax((b as any)?.order_number);
      if (ao !== bo) return ao - bo;
      const al = norm(a?.label ?? a?.name ?? a?.value ?? a?.team_id ?? a?.id);
      const bl = norm(b?.label ?? b?.name ?? b?.value ?? b?.team_id ?? b?.id);
      return al.localeCompare(bl, 'ja');
    });
    // option.value は team_id（数値を文字列化）を優先採用
    return normalizeOptions(
      sorted.map((t: any) => ({
        value: String(t?.team_id ?? t?.id ?? t?.value ?? t),
        label: String(t?.label ?? t?.name ?? t?.value ?? (t?.team_id != null ? String(t.team_id) : String(t))),
        groupLabel: isPastTeam(getTeamEndFiscalYear(t))
          ? '過去チーム'
          : norm(
              t?.groupLabel ??
                t?.team_classification_name ??
                t?.team_class_name ??
                t?.team_classification ??
                t?.class_name ??
                t?.classification_name ??
                ''
            ),
      }))
    );
  }, [teamsForSelectedDept, showPastTeams]);

  // 選択中の課（department）のラベルを解決（ID優先、無ければ division 文字列）
  const selectedDeptLabel = useMemo(() => {
    if (typeof selectedDeptId === 'number' && selectedDeptId != null) {
      const depts: any[] = Array.isArray((master as any)?.departments) ? (master as any).departments : [];
      const hit = depts.find((d: any) => Number(d?.department_id ?? d?.id) === selectedDeptId);
      const lb = norm(hit?.label ?? hit?.value ?? '');
      if (lb) return lb;
    }
    return norm(division ?? '');
  }, [selectedDeptId, master, division]);

  const { profile: userProfile } = useUserProfile();
  const preferredTeamIdValue = norm(userProfile?.preferredTeamId);

  // division 変更やチーム候補変化時に、team の初期値を「自分のチーム（候補内に在れば）」→ プルダウン先頭の順で合わせる
  const prevDivisionRef = useRef<string | undefined>(division);

  // ユーザー情報の新規登録直後は、登録前に先頭チームで保存された値より自分のチームを優先する。
  // 課の再判定→チーム候補の再読込に時間差があるため、一定時間だけ有効にして候補が揃うまで待つ。
  const forcePreferredTeamUntilRef = useRef(0);
  const [forcePreferredTick, setForcePreferredTick] = useState(0);
  useEffect(() => {
    const onUserRegistered = () => {
      forcePreferredTeamUntilRef.current = Date.now() + FORCE_PREFERRED_TEAM_WINDOW_MS;
      setForcePreferredTick((n) => n + 1);
    };
    window.addEventListener(USER_REGISTERED_EVENT, onUserRegistered);
    return () => window.removeEventListener(USER_REGISTERED_EVENT, onUserRegistered);
  }, []);

  useEffect(() => {
    if (activeTab !== 'new') return; // ★追加（hidden時に親を更新しない）

    if (forcePreferredTeamUntilRef.current > Date.now() && preferredTeamIdValue) {
      const forcedOpts = uniqueOptions(teamOpts);
      if (forcedOpts.some((o) => o.value === preferredTeamIdValue)) {
        forcePreferredTeamUntilRef.current = 0;
        if (String(team ?? '') !== preferredTeamIdValue) {
          setTeam?.(preferredTeamIdValue);
          return;
        }
      }
    }

    const opts = uniqueOptions(teamOpts);
    const values = new Set(opts.map((o) => o.value));
    const divisionChanged = prevDivisionRef.current !== division;
    const currentTeam = String(team ?? '');

    // 現在値が「過去チーム」のときは、候補から落ちて先頭へ戻される前に
    // 「過去チームも表示」を自動でONにして選択を維持する。
    if (currentTeam && !showPastTeams && !values.has(currentTeam)) {
      const allValues = new Set(
        teamsForSelectedDept.map((t: any) =>
          String(t?.team_id ?? t?.id ?? t?.value ?? t)
        )
      );
      if (allValues.has(currentTeam)) {
        setShowPastTeams(true);
        return;
      }
    }

    // division が変わった、または現在の team が候補に含まれない場合は初期化する。
    // 自分のチーム(preferredTeamId)が候補に在ればそれを、無ければ先頭項目を選ぶ。
    if (opts.length > 0 && (divisionChanged || !team || !values.has(String(team)))) {
      const next = pickInitialTeamValue(opts.map((o) => o.value), preferredTeamIdValue);
      if (next !== null && team !== next) setTeam?.(next);
    } else if (opts.length === 0 && team) {
      // 候補が無いときは値をクリア
      setTeam?.('');
    }

    prevDivisionRef.current = division;
  }, [
    activeTab,
    division,
    teamOpts,
    team,
    setTeam,
    showPastTeams,
    teamsForSelectedDept,
    preferredTeamIdValue,
    forcePreferredTick,
  ]);

  const { userOptions } = useUserOptions();

  const fallbackProfileTeamCandidates = useMemo(() => {
    const out = new Set<string>();
    const add = (v: any) => {
      const s = norm(v);
      if (s) out.add(s);
    };

    const preferredTeamId = norm(userProfile?.preferredTeamId);

    add(userProfile?.preferredTeamLabel);
    add(preferredTeamId);
    add(userProfile?.team);

    if (preferredTeamId) {
      const teams: any[] = Array.isArray((master as any)?.teams) ? (master as any).teams : [];
      const hit = teams.find((t: any) => norm(t?.team_id ?? t?.id ?? t?.value ?? '') === preferredTeamId);
      if (hit) {
        add(hit?.label);
        add(hit?.team_name);
        add(hit?.teamName);
        add(hit?.team_label);
        add(hit?.teamLabel);
        add(hit?.name);
      }
    }

    return Array.from(out);
  }, [userProfile?.preferredTeamId, userProfile?.preferredTeamLabel, userProfile?.team, master]);

  const preferredTeamCandidates = useMemo(() => {
    return buildPreferredTeamCandidates({
      users: userOptions as any[],
      selectedTeam: team,
      teamOptions: teamOpts,
      teams: Array.isArray((master as any)?.teams) ? (master as any).teams : [],
      fallbackTeamCandidates: fallbackProfileTeamCandidates,
    });
  }, [userOptions, team, teamOpts, master, fallbackProfileTeamCandidates]);

  /**
   * 課（division）から解決したラベル（例: "XQ4"）と、
   * 直接の division 文字列も候補に入れておく（ID/ラベルどちらで来ても吸収）
   */
  const reporterOpts = useDepartmentUserOptions({
    users: userOptions as any[],
    deptCandidates: [selectedDeptLabel, division],
    strict: false,
    preferredTeamCandidates,
    prioritizePreferredTeam: true,
  });

  const selectedDeptTeamIds = useMemo(() => {
    return Array.from(
      new Set(
        (teamsForSelectedDept ?? [])
          .map((t: any) => norm(t?.team_id ?? t?.id ?? t?.value ?? ''))
          .filter(Boolean)
      )
    );
  }, [teamsForSelectedDept]);

  const {
    respondentOptions: taskMemberRespondentOptions,
    customOptions: taskMemberRespondentCustomOptions,
    addOptions: addCoAuthorRespondentOptions,
  } = useCoAuthorRespondentOptions({
    loginId,
    departmentId: selectedDeptId,
    teamIds: selectedDeptTeamIds,
    officialOptions: reporterOpts,
  });
  const dbRespondentsLoaded = true;

  // 連名者候補: 課の人物 + DB保存済み respondents（末尾に追加）
  const taskMemberOpts = useMemo(() => {
    const base = uniqueOptions((reporterOpts ?? []).map(toOption));
    const tail = uniqueOptions((taskMemberRespondentOptions ?? []).map(toOption));
    return mergeCoAuthorOptionsByLabel(base, tail);
  }, [reporterOpts, taskMemberRespondentOptions]);

  const taskMemberCustomOpts = useMemo(() => {
    return uniqueOptions((taskMemberRespondentCustomOptions ?? []).map(toOption));
  }, [taskMemberRespondentCustomOptions]);

  const taskMemberOptList = useMemo(() => {
    return uniqueOptions((taskMemberOpts ?? []).map(toOption));
  }, [taskMemberOpts]);

  const taskMemberOptListRef = useRef<CoAuthorOption[]>([]);

  useEffect(() => {
    taskMemberOptListRef.current = taskMemberOptList;
  }, [taskMemberOptList]);

  const handleTaskMembersChange = useCallback(
    (next: Array<{ value: string; label: string }>) => {
      if (activeTab !== 'new') return;

      didTouchTaskMembersRef.current = true;

      const normalized = limitTaskMembers(
        sanitizeCoAuthorsByOptions(
          next,
          taskMemberOptListRef.current,
          { allowCustomValue: true }
        )
      );
      addCoAuthorRespondentOptions(normalized);
      const nextSig = coAuthorsSig(normalized);

      try {
        pendingTaskMembersSigRef.current = nextSig;
      } catch {
        pendingTaskMembersSigRef.current = String(Date.now());
      }

      // 表示更新を最優先。重い親同期・保存は後ろへ回す。
      setFields((prev) => {
        let changed = false;

        const nextFields = prev.map((field) => {
          if (field.type !== '連名者' || field.isRemovable) return field;

          const current = Array.isArray(field.value) ? field.value : [];
          if (coAuthorsSig(current) === nextSig) return field;

          changed = true;
          return { ...field, value: normalized };
        });

        return changed ? nextFields : prev;
      });

      if (taskMembersSaveTimerRef.current != null) {
        window.clearTimeout(taskMembersSaveTimerRef.current);
      }

      taskMembersSaveTimerRef.current = window.setTimeout(() => {
        setTaskMembers?.(normalized);
        writeTaskMembersCache(taskMembersCacheKey, normalized);
      }, 100);
    },
    [activeTab, addCoAuthorRespondentOptions, setTaskMembers, taskMembersCacheKey]
  );

  const approver3Opts = useMemo(() => {
    const arr = (master?.approvers ?? []).map((x: any) => norm(x?.label ?? x?.value ?? x)).filter(Boolean);
    return normalizeOptions(arr.map((s) => ({ value: s, label: s })));
  }, [master]);

  const approverAMOpts = useMemo(() => {
    const arr = (master?.approverAMs ?? []).map((x: any) => norm(x?.label ?? x?.value ?? x)).filter(Boolean);
    return normalizeOptions(arr.map((s) => ({ value: s, label: s })));
  }, [master]);

  /* ---------- 属性（ATTRIBUTE_MANAGE） ---------- */

  const allAttributes: AttrFromMaster[] = useMemo(() => {
    const list = ((master as any)?.attributeManage ?? []) as AttrFromMaster[];
    return (list ?? []).map((x) => ({
        ...x,
        attr_type: toAttrTypeCode(x?.attr_type),
        multiple_select: toMultiFlag(x?.multiple_select)
      }));
  }, [master]);

  const currentTeamId = useMemo(() => {
    const hit = uniqueOptions(teamOpts).find((o) => norm(o.value) === norm(team) || norm(o.label) === norm(team));
    const n = Number(hit?.value ?? team);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [team, teamOpts]);

  const scopedManagedAttrsSorted = useMemo(
    () =>
      selectManagedAttrsForScope(
        (allAttributes ?? []).filter((a) => !FIXED_BASE_NAMES.has(a.attr_name)),
        {
          departmentId: selectedDeptId,
          teamId: currentTeamId,
        }
      ),
    [allAttributes, selectedDeptId, currentTeamId]
  );

  const hiddenScopedAttrNameSet = useMemo(
    () =>
      new Set(
        scopedManagedAttrsSorted
          .filter((a) => !isManagedAttrVisible(a))
          .map((a) => normalizeManagedAttrNameKey(a.attr_name))
      ),
    [scopedManagedAttrsSorted]
  );

  const commonAttrsSorted = useMemo(
    () =>
      (allAttributes ?? [])
        .filter((a) => isCommonDisplayManagedAttr(a))
        .filter((a) => !hiddenScopedAttrNameSet.has(normalizeManagedAttrNameKey(a.attr_name)))
        .sort(
          (a, b) =>
            Number(a.order_number ?? 9999) - Number(b.order_number ?? 9999) || a.attr_name.localeCompare(b.attr_name, 'ja')
        ),
    [allAttributes, hiddenScopedAttrNameSet]
  );

  const scopedCommonAttrsSorted = useMemo(() => {
    return selectManagedAttrsForScope(commonAttrsSorted, {
      departmentId: selectedDeptId,
      teamId: currentTeamId,
      includeUnscopedFallback: true,
    }).filter((a) => isManagedAttrVisible(a));
  }, [commonAttrsSorted, selectedDeptId, currentTeamId]);

  const dynamicAttrsSorted = useMemo(
    () =>
      (allAttributes ?? [])
        .filter((a) => isFreeInputManagedAttr(a))
        .filter((a) => !hiddenScopedAttrNameSet.has(normalizeManagedAttrNameKey(a.attr_name)))
        .filter((a) => !FIXED_BASE_NAMES.has(a.attr_name)),
    [allAttributes, hiddenScopedAttrNameSet]
  );

  // 選択中チーム/課に応じて動的属性をスコープする。
  // 優先順位は「チーム専用 > 課共通(team_id=0) > 全体共通(0,0)」。
  // これにより、team_id=41 に工順が無い場合でも department_id=14/team_id=0 の工順を表示できる。
  const effectiveDynamicAttrsForTeam = useMemo(() => {
    return selectManagedAttrsForScope(dynamicAttrsSorted, {
      departmentId: selectedDeptId,
      teamId: currentTeamId,
      includeUnscopedFallback: true,
    }).filter((a) => isManagedAttrVisible(a));
  }, [dynamicAttrsSorted, selectedDeptId, currentTeamId]);

  const scopedAttrNameSet = useMemo(() => {
    return new Set(
      effectiveDynamicAttrsForTeam.map((a) => normalizeManagedAttrNameKey(a.attr_name))
    );
  }, [effectiveDynamicAttrsForTeam]);

  const visibleCommonAttrsSorted = useMemo(
    () =>
      scopedCommonAttrsSorted.filter(
        (a) => !scopedAttrNameSet.has(normalizeManagedAttrNameKey(a.attr_name))
      ),
    [scopedCommonAttrsSorted, scopedAttrNameSet]
  );

  const allDynamicAttrsSorted = useMemo(() => [...visibleCommonAttrsSorted, ...effectiveDynamicAttrsForTeam], [
    visibleCommonAttrsSorted,
    effectiveDynamicAttrsForTeam
  ]);

  /* ---------- 初期 fields ---------- */
  const initialTaskMembersValue = (() => {
    // 最初は空にしたいので、cache からは復元しない
    const raw = Array.isArray(taskMembers) ? taskMembers : [];
    if (!dbRespondentsLoaded) return limitTaskMembers(normalizeCoAuthorsValue(raw));
    return limitTaskMembers(sanitizeCoAuthorsByOptions(raw, taskMemberOptList, { allowCustomValue: false }));
  })();


  const initialFields: DynamicFieldItem[] = [
    {
      id: 'reporter-init',
      type: '記入者',
      label: '記入者',
      value: reporter?.trim() ? reporter : '',
      isRequired: true,
      isRemovable: false,
      divisionContext: division,
      options: reporterOpts
    },
    {
      id: 'taskmembers-base',
      type: '連名者',
      label: '連名者',
      // props未提供(undefined)でもタブ切替で消えないよう cache から初期化（正規化）
      value: Array.isArray(initialTaskMembersValue) ? initialTaskMembersValue : [],
      isRemovable: false,
      divisionContext: division,
      // 候補は「課（division）で絞った userOptions」＋「DB保存済み respondents」
      options: taskMemberOptList
    },
    {
      id: 'approveram-init',
      type: '承認AM',
      label: 'AM',
      value: approverAM ?? '',
      isRemovable: false,
      divisionContext: division,
      options: approverAMOpts
    },
    {
      id: 'daterange-init',
      type: '開始終了日付',
      label: '開始日付',
      value: { from: startDate || '', to: endDate || '' },
      isRequired: true,
      isRemovable: false,
      divisionContext: division
    }
  ];

  const [fields, setFields] = useState<DynamicFieldItem[]>(initialFields);

  // Hydration gate（復元完了まで親へ通知しない）
  const [isHydrated, setIsHydrated] = useState(false);

  const newDraftMapRef = useRef<Map<string, any>>(new Map());
  const newDraftAppliedSigRef = useRef<string>('');

  const draftKeyByAttrId = useCallback((attrId?: number | string): string | null => {
    const id = Number(attrId);
    return Number.isFinite(id) ? `id:${id}` : null;
  }, []);

  const draftKeyByName = useCallback((name?: string): string | null => {
    const key = norm(name ?? '');
    return key ? `name:${key}` : null;
  }, []);

  const getDraftValueForField = useCallback((field: { type?: string; attr_id?: number | string }): any | undefined => {
    const map = newDraftMapRef.current;
    const idKey = draftKeyByAttrId(field.attr_id);
    if (idKey && map.has(idKey)) return map.get(idKey);

    const nameKey = draftKeyByName(field.type);
    if (nameKey && map.has(nameKey)) return map.get(nameKey);

    return undefined;
  }, [draftKeyByAttrId, draftKeyByName]);

  const getDraftValueForAttr = useCallback((attr: { attr_name?: string; attr_id?: number | string }): any | undefined => {
    const map = newDraftMapRef.current;
    const idKey = draftKeyByAttrId(attr.attr_id);
    if (idKey && map.has(idKey)) return map.get(idKey);

    const nameKey = draftKeyByName(attr.attr_name);
    if (nameKey && map.has(nameKey)) return map.get(nameKey);

    return undefined;
  }, [draftKeyByAttrId, draftKeyByName]);

  // ★無限レンダリング防止：親通知（snapshot/freeWords/attrs）を差分比較する
  const prevFieldsSnapshotJsonRef = useRef<string>('');
  const prevFreeWordsJsonRef = useRef<string>('');
  const prevAttrsJsonRef = useRef<string>('');

  // ★保存用スナップショット（options 等の“派生”を除外して安定化）
  const buildPersistSnapshot = (src: DynamicFieldItem[]) => {
    // ★deep clone（可能なら structuredClone、無ければ JSON でフォールバック）
    const deepClone = <T,>(v: T): T => {
      const sc = (globalThis as any).structuredClone;
      if (typeof sc === 'function') {
        try {
          return sc(v);
        } catch {
          /* fallthrough */
        }
      }
      try {
        return JSON.parse(JSON.stringify(v));
      } catch {
        return v;
      }
    };

    return (src ?? []).map((f) => {
      const value = (() => {
        // 連名者は {value,label}[] を安定保存
        if (f.type === '連名者') {
          const raw = Array.isArray(f.value) ? f.value : [];
          return limitTaskMembers(normalizeCoAuthorsValue(raw));
        }

        if (Array.isArray(f.value)) {
          return f.value.map((x: any) => {
            if (x == null) return '';
            if (typeof x === 'object') {
              // select系（value/label を持つもの）は {value,label} に正規化
              if ('value' in (x as any) || 'label' in (x as any)) {
                return {
                  value: String((x as any).value ?? ''),
                  label: String((x as any).label ?? (x as any).value ?? '')
                };
              }

              // その他の object（例: 課題メンバー）はそのまま deep clone
              return deepClone(x);
            }
            return String(x);
          });
        }

        if (typeof f.value === 'object' && f.value !== null) {
          const o: any = f.value as any;

          // date range
          if ('from' in o || 'to' in o) {
            return {
              from: String(o.from ?? ''),
              to: String(o.to ?? '')
            };
          }

          // select / datalist
          if ('value' in o || 'label' in o) {
            return {
              value: String(o.value ?? ''),
              label: String(o.label ?? o.value ?? '')
            };
          }

          // unknown object → deep clone（循環は try/catch）
          return deepClone(o);
        }

        return String(f.value ?? '');
      })();

      const attrId = Number((f as any)?.attr_id);

      return {
        id: f.id,
        type: f.type,
        label: f.label ?? '',
        value,
        isRemovable: !!f.isRemovable,
        isRequired: !!f.isRequired,
        attr_id: Number.isFinite(attrId) ? attrId : undefined
      };
    });
  };

  /* ---------- draft復元（空だけ埋める） ---------- */
  /* ---------- draft復元（空だけ埋める） ---------- */
  useEffect(() => {
    if (activeTab !== 'new') return; //（hidden時に state を書き換えない）
    if (!Array.isArray(initialNewDraftFields) || initialNewDraftFields.length === 0) return;

    let sig = '';
    try {
      sig = JSON.stringify(
        (initialNewDraftFields ?? []).map((f) => ({
          id: (f as any)?.attr_id ?? null,
          t: norm(f?.type),
          r: !!f?.isRemovable,
          v: f?.value
        }))
      );
    } catch {
      sig = String(Date.now());
    }
    if (newDraftAppliedSigRef.current === sig) return;
    newDraftAppliedSigRef.current = sig;

    const map = new Map<string, any>();
    initialNewDraftFields.forEach((f) => {
      const typeRaw = norm(f?.type);
      if (!typeRaw) return;

      // 旧キー吸収: 課題メンバー → 連名者
      const type = typeRaw === '課題メンバー' ? '連名者' : typeRaw;

      // isRemovable=true は今回は復元対象外（必要なら後で拡張）
      if (f?.isRemovable) return;

      // 開始/終了日は復元しない
      if (type === '開始終了日付') return;

      const idKey = draftKeyByAttrId((f as any)?.attr_id);
      const nameKey = draftKeyByName(type);

      if (idKey) {
        map.set(idKey, f?.value);
      } else if (nameKey) {
        map.set(nameKey, f?.value);
      }
    });

    newDraftMapRef.current = map;

    // ★辞書を作った直後に、既存 fields の“空値”だけ復元値で埋める（入ってる値は潰さない）
    setFields((prev) => {
      let changed = false;

      const next = prev.map((x) => {
        if (!x || x.isRemovable) return x;
        const draft = getDraftValueForField(x);
        if (typeof draft === 'undefined') return x;

        // 連名者は後段で別処理（親同期/キャッシュも必要）
        if (norm(x.type) === '連名者') return x;

        if (isBlankValue(draft)) return x;

        // 既に値が入っているなら潰さない
        if (!isBlankValue(x.value)) return x;

        changed = true;
        return { ...x, value: draft };
      });

      return changed ? next : prev;
    });

    // 固定欄「連名者」にも適用する（旧: 課題メンバー）
    const draftMembersRaw = map.get(draftKeyByName('連名者') ?? '');
    const draftMembers = limitTaskMembers(normalizeCoAuthorsValue(draftMembersRaw));

    if (Array.isArray(draftMembers) && draftMembers.length > 0) {
      // 親にも同期（親が未保持なら復元値を入れる）
      const parentMembers = Array.isArray(taskMembers)
        ? limitTaskMembers(normalizeCoAuthorsValue(taskMembers))
        : [];

      const parentSig = coAuthorsSig(parentMembers);
      const draftSig = coAuthorsSig(draftMembers);

      // 親が空だけでなく「内容が違う」場合も、draft を優先して親へ同期
      if (draftSig !== parentSig) {
        setTaskMembers?.(draftMembers);
      }

      // cacheにも反映（props未提供のタブでも復元できる）
      writeTaskMembersCache(taskMembersCacheKey, draftMembers);
      addCoAuthorRespondentOptions(draftMembers);

      setFields((prev) =>
        prev.map((x) => {
          if (x.type !== '連名者' || x.isRemovable) return x;

          const cur = Array.isArray(x.value) ? limitTaskMembers(normalizeCoAuthorsValue(x.value)) : [];
          // 既に入ってるなら潰さない
          if (cur.length > 0) return x;

          return { ...x, value: draftMembers };
        })
      );
    }
  }, [activeTab, initialNewDraftFields, taskMembers, setTaskMembers, taskMembersCacheKey, addCoAuthorRespondentOptions, draftKeyByAttrId, draftKeyByName, getDraftValueForField]);


  /* ---------- 課題メンバー cache ---------- */
  useEffect(() => {
    if (activeTab !== 'new') return; // （hidden時に cache を触らない）
    // ユーザー操作後は handleTaskMembersChange 側の debounce 保存に任せる。
    // fields 変更直後に同期保存すると、入力表示の体感速度が落ちる。
    if (didTouchTaskMembersRef.current) return;

    const f = fields.find((x) => x.type === '連名者' && !x.isRemovable);
    const cur = Array.isArray(f?.value) ? limitTaskMembers(normalizeCoAuthorsValue(f!.value)) : [];

    // 空で上書きしない（復元前の初期renderで cache を潰すのを防ぐ）
    if (cur.length === 0) {
      const existing = readTaskMembersCache(taskMembersCacheKey);
      if (existing.length > 0) return;
    }

    writeTaskMembersCache(taskMembersCacheKey, cur);
  }, [activeTab, fields, taskMembersCacheKey]);

  /* ---------- 連名者：旧データ（自由入力）を options に合わせて自動修復 ---------- */
  useEffect(() => {
    if (activeTab !== 'new') return; //（hidden時に state を書き換えない）
    // ユーザー操作後に自動補正を走らせると、入力直後の表示が遅くなる。
    // 初期復元・DB候補ロード直後の補正だけに限定する。
    if (didTouchTaskMembersRef.current) return;
    if (!dbRespondentsLoaded) return;
    if (!Array.isArray(taskMemberOptList) || taskMemberOptList.length === 0) return;

    const optList = taskMemberOptList;

    // fields の連名者（base）を sanitize
    setFields((prev) => {
      let changed = false;

      const next = prev.map((f) => {
        if (f.type !== '連名者' || f.isRemovable) return f;

        const cur = Array.isArray(f.value) ? f.value : [];
        const sanitized = limitTaskMembers(
          sanitizeCoAuthorsByOptions(cur, optList, {
            allowCustomValue: didTouchTaskMembersRef.current
          })
        );

        if (coAuthorsSig(cur) === coAuthorsSig(sanitized)) return f;

        changed = true;
        return { ...f, value: sanitized };
      });

      return changed ? next : prev;
    });

    // 親/キャッシュ側も sanitize（幽霊値が保存され続けるのを防ぐ）
    if (typeof taskMembers !== 'undefined') {
      const parentCur = Array.isArray(taskMembers) ? taskMembers : [];
      const parentSanitized = limitTaskMembers(
        sanitizeCoAuthorsByOptions(parentCur, optList, {
          allowCustomValue: didTouchTaskMembersRef.current
        })
      );

      if (coAuthorsSig(parentCur) !== coAuthorsSig(parentSanitized)) {
        setTaskMembers?.(parentSanitized as any);
        writeTaskMembersCache(taskMembersCacheKey, parentSanitized);
      }
    }
  }, [activeTab, taskMemberOptList, dbRespondentsLoaded, taskMembers, setTaskMembers, taskMembersCacheKey]);

  /* ---------- Hydration完了判定（fields揃うまで親通知を止める） ---------- */
  useEffect(() => {
    if (activeTab !== 'new') return;
    if (isHydrated) return;
    if (!master) return;

    // base dynamic fields が揃ったらOK（0件なら即OK）
    const ready =
      allDynamicAttrsSorted.length === 0 ||
      allDynamicAttrsSorted.every((a) => fields.some((f) => !f.isRemovable && sameName(f.type, a.attr_name)));

    if (!ready) return;

    setIsHydrated(true);
    onHydrated?.();
  }, [activeTab, isHydrated, master, allDynamicAttrsSorted, fields, onHydrated]);

  /* ---------- ヘルパ ---------- */

  const clsNameOf = (type: string) => clsMap[type] ?? `free-${slugify(type)}`;

  const defaultValueForAttr = (attrType?: number | string, multiple?: number | string | boolean) => {
    const t = toAttrTypeCode(attrType);
    const multi = toMultiFlag(multiple);
    if (t === 5 || t === 6 || t === 3 || t === 4) return '';
    if (t === 12) return [];
    if (t === 11) return multi === 1 ? [] : '';
    if (t === 13) return { value: '', label: '' };
    return '';
  };

  const sameOptions = (a?: { value: string; label: string }[], b?: { value: string; label: string }[]) =>
    a === b || (!!a && !!b && a.length === b.length && a.every((x, i) => x.value === b[i].value && x.label === b[i].label));

  const renderInput = (f: DynamicFieldItem, attrType?: number | string) => {
    const t = toAttrTypeCode(attrType);

    if (t === 2) {
      return (
        <textarea
          id={f.id}
          value={String(f.value ?? '')}
          rows={4}
          onChange={(e) => setFields((p) => p.map((x) => (x.id === f.id ? { ...x, value: e.target.value } : x)))}
          style={{ width: '100%', padding: '8px 10px', border: `1px solid ${isDark ? '#334155' : '#ddd'}`, borderRadius: 8 }}
        />
      );
    }

    if (t === 3) {
      return (
        <input
          id={f.id}
          type="text"
          inputMode="numeric"
          pattern="-?\\d*"
          value={String(f.value ?? '')}
          onChange={(e) =>
            setFields((p) => p.map((x) => (x.id === f.id ? { ...x, value: sanitizeIntStrict(e.target.value) } : x)))
          }
          style={{ width: '100%', height: 36, padding: '6px 10px', border: `1px solid ${isDark ? '#334155' : '#ddd'}`, borderRadius: 8 }}
        />
      );
    }

    if (t === 5) {
      return (
        <FreeDatePicker
          id={f.id}
          freeOptionsList={[{ label: f.label ?? '日付' } as OptionsBase]}
          value={String(f.value ?? '')}
          onChange={(out: any) => setFields((p) => p.map((x) => (x.id === f.id ? { ...x, value: String(out ?? '') } : x)))}
        />
      );
    }

    if (t === 6) {
      return (
        <input
          id={f.id}
          type="time"
          value={String(f.value ?? '')}
          onChange={(e) => setFields((p) => p.map((x) => (x.id === f.id ? { ...x, value: e.target.value } : x)))}
          style={{ width: '100%', height: 36, padding: '6px 10px', border: `1px solid ${isDark ? '#334155' : '#ddd'}`, borderRadius: 8 }}
        />
      );
    }

    if (t === 4) {
      const toAsciiDecimal = (raw: string) => {
        let s = (raw ?? '').toString().normalize('NFKC').replace(/[,，、。]/g, '.').replace(/[^\d.\-]/g, '');
        const dot = s.indexOf('.');
        if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '');
        const neg = s.startsWith('-');
        s = s.replace(/-/g, '');
        return (neg ? '-' : '') + s;
      };

      return (
        <input
          id={f.id}
          type="text"
          inputMode="decimal"
          value={String(f.value ?? '')}
          onChange={(e) => setFields((p) => p.map((x) => (x.id === f.id ? { ...x, value: toAsciiDecimal(e.target.value) } : x)))}
          style={{ width: '100%', height: 36, padding: '6px 10px', border: `1px solid ${isDark ? '#334155' : '#ddd'}`, borderRadius: 8 }}
        />
      );
    }

    return (
      <input
        id={f.id}
        type="text"
        value={String(f.value ?? '')}
        onChange={(e) => setFields((p) => p.map((x) => (x.id === f.id ? { ...x, value: e.target.value } : x)))}
        style={{ width: '100%', height: 36, padding: '6px 10px', border: `1px solid ${isDark ? '#334155' : '#ddd'}`, borderRadius: 8 }}
      />
    );
  };

  const renderDynamic = (f: DynamicFieldItem): React.ReactNode => {
    const meta = allDynamicAttrsSorted.find((a) => sameName(a.attr_name, f.type));
    const t = toAttrTypeCode(meta?.attr_type);
    const opts = uniqueOptions((f.options ?? []).map(toOption));

    // 11: select（multiple_select=1 で複数、0 で単一）
    if (t === 11) {
      const isMulti = toMultiFlag(meta?.multiple_select) === 1;

      // 単一選択: CommonSelect（Option | null）
      if (!isMulti) {
        return (
          <CommonSelect
            id={f.id}
            options={opts}
            value={toSelected(f.value, opts)}
            placeholder={`-- ${f.label} --`}
            optionDescriptions={
              f.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME ? reportTypeScopeDisplayByOptionId : undefined
            }
            defaultOptionDescription={
              f.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME ? DEFAULT_PUBLIC_SCOPE_DISPLAY : undefined
            }
            onChange={(next: CSOption | null) => {
              // 親への副作用（固定項目ではない attr のみここで反映）
              if (!f.isRemovable && next) {
                if (f.type === FIXED_ATTR_NAME_SHIMUKE) setShikimuke?.(next.label);
                if (f.type === FIXED_ATTR_NAME_LOT) setLot?.(next.label);
                if (f.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME) setReportType?.(next.value);
              }
              setFields((p) =>
                p.map((x) =>
                  x.id === f.id
                    ? next
                      ? { ...x, value: { value: next.value, label: next.label } }
                      : { ...x, value: '' }
                    : x
                )
              );
            }}
          />
        );
      }

      const selected = sanitizeSelectArrayByOptions(f.value, opts);
      // 複数選択: CheckboxDropdownSelect に「検索入力」
      return (
        <CheckboxDropdownSelect
          id={f.id}
          placeholder={`-- ${f.label ?? f.type} --`}
          options={opts as CDSOption[]}
          value={selected as CDSOption[]}
          optionDescriptions={
            f.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME
              ? reportTypeScopeDisplayByOptionId
              : undefined
          }
          defaultOptionDescription={
            f.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME ? DEFAULT_PUBLIC_SCOPE_DISPLAY : undefined
          }
          onChange={(next) => {
            if (activeTab !== 'new') return;

            markSelectTouched(f.type);
            const normalized = sanitizeSelectArrayByOptions(next, opts);
            setFields((p) => p.map((x) => (x.id === f.id ? { ...x, value: normalized } : x)));
          }}
        />
      );
    }

    // 12: checkbox（MultiSelectWithCheckboxes）
    if (t === 12) {
      const suggestions: Suggestion[] = toSuggestions(opts);
      return (
        <MultiSelectWithCheckboxes
          suggestions={suggestions}
          placeholder={`-- ${f.label} --`}
          initialValue={Array.isArray(f.value) ? f.value : []}
          onChange={(v) => setFields((p) => p.map((x) => (x.id === f.id ? { ...x, value: v } : x)))}
        />
      );
    }

    // 13: datalist（自由入力可）
    if (t === 13) {
      const listId = `${f.id}-datalist`;
      const display =
        typeof f.value === 'object' ? String((f.value as any).label ?? (f.value as any).value ?? '') : String(f.value ?? '');
      const map = new Map(opts.map((o) => [o.label, o.value]));
      const openPicker = (input: HTMLInputElement) => {
        try {
          (input as any).showPicker?.();
        } catch {
          /* no-op */
        }
      };

      return (
        <div className="datalist-wrapper">
          <input
            id={f.id}
            type="text"
            list={listId}
            value={display}
            placeholder={f.label}
            onChange={(e) => {
              const txt = e.target.value;
              setFields((p) =>
                p.map((x) =>
                  x.id === f.id ? { ...x, value: map.has(txt) ? { value: map.get(txt)!, label: txt } : txt } : x
                )
              );
            }}
            onKeyDown={(e) => {
              if ((e.key === 'ArrowDown' || e.key === 'F4') && SUPPORTS_DATALIST_PICKER) {
                const el = datalistInputRefs.current[f.id];
                if (el) openPicker(el);
              }
            }}
            onMouseDown={(e) => {
              const input = e.currentTarget as HTMLInputElement;
              if (SUPPORTS_DATALIST_PICKER) openPicker(input);
            }}
            ref={(el) => {
              datalistInputRefs.current[f.id] = el;
            }}
            autoComplete="off"
          />
          <datalist id={listId}>
            {opts.map((o, idx) => (
              <option key={`${o.value}__${idx}`} value={o.label} data-value={o.value} />
            ))}
          </datalist>
        </div>
      );
    }

    // それ以外（text/textarea/int/decimal/date/time）
    return renderInput(f, meta?.attr_type);
  };

  const wrapField = (f: DynamicFieldItem) => {
    const cls = clsNameOf(f.type);

    const labelEl =
      f.type !== '開始終了日付' && f.type !== '記入者' ? (
        <label htmlFor={f.id} className={f.isRequired ? 'required-label' : ''}>
          {f.label}&nbsp;{SyncIcon}
        </label>
      ) : f.type === '記入者' ? (
        <div className="start-date-icon">
          <label htmlFor={f.id} className={f.isRequired ? 'required-label' : ''}>
            {f.label}
          </label>
          &nbsp;{SyncIcon}
        </div>
      ) : null;

    return (
      <div
        key={f.id}
        className={`form-field dynamic-field-wrapper add-parent-${cls}`}
      >
        {labelEl}
        <div style={{ position: 'relative' }}>
          {f.type === '記入者' ? (
            <CommonSelect
              id={f.id}
              placeholder={`-- ${f.label} --`}
              options={uniqueOptions(reporterOpts)}
              value={toSelected(f.value, uniqueOptions(reporterOpts))}
              allowFreeText
              commitInputOnBlur
              onChange={(opt: CSOption | null) => {
                const v = norm(opt?.value ?? '');
                setReporter?.(v); // 親の reporter を更新
                setFields((p) => p.map((x) => (x.id === f.id ? { ...x, value: v } : x)));
              }}
            />
          ) : f.type === '承認AM' ? (
            <CommonSelect
              id={f.id}
              placeholder={`-- ${f.label} --`}
              options={uniqueOptions(approverAMOpts)}
              value={toSelected(f.value, uniqueOptions(approverAMOpts))}
              onChange={(opt: CSOption | null) => {
                const v = norm(opt?.value ?? '');
                setApproverAM?.(v);
                setFields((p) => p.map((x) => (x.id === f.id ? { ...x, value: v } : x)));
              }}
            />
          ) : f.type === 'チーム' ? (
            // 追加の「チーム」行（isRemovable=true）用：親 setTeam は呼ばない
            <CommonSelect
              id={f.id}
              placeholder={`-- ${f.label ?? 'チーム'} --`}
              options={uniqueOptions(teamOpts)}
              value={toSelected(f.value, uniqueOptions(teamOpts))}
              onChange={(opt: CSOption | null) => {
                const v = norm(opt?.value ?? '');
                setFields((p) => p.map((x) => (x.id === f.id ? { ...x, value: v } : x)));
              }}
            />
          ) : f.type === '承認-3' ? (
            // 追加の「承認-3」行（isRemovable=true）用：親 setApprover3 は呼ばない
            <CommonSelect
              id={f.id}
              placeholder={`-- ${f.label ?? '職制'} --`}
              options={uniqueOptions(approver3Opts)}
              value={toSelected(f.value, uniqueOptions(approver3Opts))}
              onChange={(opt: CSOption | null) => {
                const v = norm(opt?.value ?? '');
                setFields((p) => p.map((x) => (x.id === f.id ? { ...x, value: v } : x)));
              }}
            />
            ) : f.type === '連名者' ? (
              <CheckboxDropdownSelectWithAll
                id={f.id}
                placeholder="-- 連名者 --"
                options={taskMemberOptList}
                customOptions={taskMemberCustomOpts}
                value={Array.isArray(f.value) ? f.value : []}
                allowCustomValue
                searchPlaceholder="検索 / 入力で追加"
                maxSelected={TASK_MEMBERS_MAX_SELECTED}
                maxSelectedMessage={TASK_MEMBERS_MAX_SELECTED_MESSAGE}
                onChange={handleTaskMembersChange}
              />
          ) : f.type === '開始終了日付' ? (
            <WeeklyDatePicker
              start={f.value?.from}
              end={f.value?.to}
              onChange={(range) => {
                if (activeTab !== 'new') return;
                setFields((p) =>
                  p.map((x) =>
                    x.id === f.id ? { ...x, value: range } : x
                  )
                );
                // ここで親も同期（useEffectでやらない）
                if (setStartDate && startDate !== range.from) setStartDate(range.from);
                if (setEndDate && endDate !== range.to) setEndDate(range.to);
              }}
              enlightSyncEnabled={enlightSyncEnabled}
            />
          ) : (
            renderDynamic(f)
          )}
        </div>

        {f.isRemovable && (
          <button className="delete-button" type="button" onClick={() => setFields((p) => p.filter((x) => x.id !== f.id))} />
        )}
      </div>
    );
  };

  /* ---------- マスターの options 同期（動的属性も一括） ---------- */
  useEffect(() => {
    if (activeTab !== 'new') return; // ★追加（hidden時に state を書き換えない）
    setFields((prev) => {
      let changed = false;
      const next = prev.map((f) => {
        let nextField = f;

        // 記入者/承認AM の options 更新
        if (nextField.type === '記入者' && !sameOptions(nextField.options, reporterOpts)) {
          changed = true;
          nextField = { ...nextField, options: reporterOpts };
        }
        if (nextField.type === '承認AM' && !sameOptions(nextField.options, approverAMOpts)) {
          changed = true;
          nextField = { ...nextField, options: approverAMOpts };
        }

        // 動的属性の options / required / attr_id 更新
        const meta = allDynamicAttrsSorted.find((a) => sameName(a.attr_name, nextField.type));
        if (meta && !FIXED_BASE_NAMES.has(nextField.type)) {
          const nextIsRequired = !nextField.isRemovable && Number((meta as any).input_required ?? 0) === 1;
          if (nextField.isRequired !== nextIsRequired || nextField.attr_id !== Number(meta.attr_id)) {
            changed = true;
            nextField = { ...nextField, isRequired: nextIsRequired, attr_id: Number(meta.attr_id) };
          }
        }

        if (meta && isSelectType(meta.attr_type)) {
          const bucketOpts = optionsFromBuckets(master, meta.attr_name, Number(meta.attr_id), selectedDeptId, currentTeamId);
          const baseOpts = bucketOpts.length ? bucketOpts : normalizeOptions(meta.options);
          const nextOpts = applyCommonFirstOption(baseOpts, meta.attr_name);
          if (!sameOptions(nextField.options, nextOpts)) {
            changed = true;
            nextField = { ...nextField, options: nextOpts };
          }
        }

        return nextField;
      });
      return changed ? next : prev;
    });
  }, [activeTab, reporterOpts, approverAMOpts, allDynamicAttrsSorted, master, selectedDeptId, currentTeamId]);

  /* ---------- division 変更で値リセット（型に応じたデフォルト） ---------- */
  useEffect(() => {
    if (activeTab !== 'new') return; // ★追加（hidden時に state を書き換えない）
    setFields((prev) => {
      let changed = false;
      const next = prev.map((f) => {
        if (f.divisionContext === division) return f;

        if (f.type === '連名者' && !f.isRemovable) {
          // propsが未提供(undefined)なら、子のstateを潰さない
          if (typeof taskMembers === 'undefined') return f;

          const curVal = Array.isArray(f.value)
            ? limitTaskMembers(normalizeCoAuthorsValue(f.value))
            : [];

          const nextVal = Array.isArray(taskMembers)
            ? limitTaskMembers(normalizeCoAuthorsValue(taskMembers))
            : [];

          // ユーザーが触った後は、親の値で巻き戻さない（✅が外れる原因を潰す）
          if (didTouchTaskMembersRef.current) {
            return f;
          }

          // 親が「少ない値」で来ても、子（fields）がより多く持っていて、
          // かつ nextVal が curVal の部分集合なら、子を縮めない
          if (nextVal.length < curVal.length && isCoAuthorsSubset(nextVal, curVal)) {
            return f;
          }

          // まだ触っていない（初期復元）なら、親→子を許可
          return sameJson(curVal, nextVal) ? f : { ...f, value: nextVal };
        }

        const meta = allDynamicAttrsSorted.find((a) => sameName(a.attr_name, f.type));
        const def = meta
          ? defaultValueForAttr(meta.attr_type, meta.multiple_select)
          : f.type === '課題メンバー'
          ? []
          : f.type === '承認AM' || f.type === '記入者'
          ? ''
          : f.type === '開始終了日付'
          ? { from: '', to: '' }
          : f.value;

        changed = true;
        return { ...f, divisionContext: division, value: def };
      });
      return changed ? next : prev;
    });
  }, [activeTab, division, allDynamicAttrsSorted]);

  /* ---------- 期間の親同期 + スナップショット通知 ---------- */
  useEffect(() => {
    if (activeTab !== 'new') return; // ★追加（hidden時に親を更新しない）
    if (!isHydrated) return; // ★追加（復元完了まで通知しない）

    const dr = fields.find((f) => f.type === '開始終了日付' && !f.isRemovable)?.value;
    if (dr && setStartDate && setEndDate) {
      if (startDate !== dr.from) setStartDate(dr.from);
      if (endDate !== dr.to) setEndDate(dr.to);
    }

    // 期間の親同期 + スナップショット通知 useEffect の中
    if (onFieldsSnapshot) {
      const activeDynamicNameSet = new Set(allDynamicAttrsSorted.map((a) => normalizeManagedAttrNameKey(a.attr_name)));
      const snap = buildPersistSnapshot(
        fields.filter((f) => {
          if (FIXED_BASE_NAMES.has(f.type)) return true;
          return activeDynamicNameSet.has(normalizeManagedAttrNameKey(f.type));
        })
      );

      let json: string | null = null;
      try {
        json = JSON.stringify(snap);
      } catch (e) {
        console.warn('[onFieldsSnapshot] JSON stringify failed. skip notify.', e);
      }

      if (json && prevFieldsSnapshotJsonRef.current !== json) {
        prevFieldsSnapshotJsonRef.current = json;
        onFieldsSnapshot(snap);
      }
    }
  }, [activeTab, isHydrated, fields, allDynamicAttrsSorted, onFieldsSnapshot, setStartDate, setEndDate, startDate, endDate]);

  /* ---------- 親 → 固定フィールド反映（記入者・課題メンバー・期間） ---------- */
  useEffect(() => {
    if (activeTab !== 'new') return; // （hidden時に state を書き換えない）

    setFields((prev) =>
      prev.map((f) => {
        if (f.type === '記入者' && !f.isRemovable) {
          const nextVal = reporter || '';
          return f.value === nextVal ? f : { ...f, value: nextVal };
        }

        if (f.type === '連名者' && !f.isRemovable) {
          // propsが未提供(undefined)なら、子のstateを潰さない
          if (typeof taskMembers === 'undefined') return f;

          const curVal = Array.isArray(f.value)
            ? limitTaskMembers(normalizeCoAuthorsValue(f.value))
            : [];

          const nextVal = Array.isArray(taskMembers)
            ? limitTaskMembers(normalizeCoAuthorsValue(taskMembers))
            : [];

          // ユーザーが触った後は、親の値で巻き戻さない（✅が外れる原因を潰す）
          if (didTouchTaskMembersRef.current) {
            return f;
          }

          // まだ触っていない（初期復元）なら、親→子を許可
          return sameJson(curVal, nextVal) ? f : { ...f, value: nextVal };
        }

        if (f.type === '開始終了日付' && !f.isRemovable) {
          const nextVal = { from: startDate || '', to: endDate || '' };
          return f?.value?.from === nextVal.from && f?.value?.to === nextVal.to
            ? f
            : { ...f, value: nextVal };
        }

        return f;
      })
    );
  }, [activeTab, reporter, taskMembers, startDate, endDate]);

  /* ---------- DB 上の動的属性を自動反映（存在/非存在の同期） ---------- */
  useEffect(() => {
    if (activeTab !== 'new') return; // ★追加（hidden時に state を書き換えない）
    setFields((prev) => {
      let next = [...prev];

      // 1) 既存の自動ベース欄のうち、DBに無いものを削除
      const allDynamicNameSet = new Set(allDynamicAttrsSorted.map((a) => norm(a.attr_name)));
      next = next.filter((f) => {
        if (FIXED_BASE_NAMES.has(f.type)) return true;
        return allDynamicNameSet.has(norm(f.type));
      });

      // 2) DB上にある動的属性を全て、画面に無ければ追加
      let added = false;
      allDynamicAttrsSorted.forEach((attr) => {
        const exists = next.some((f) => sameName(f.type, attr.attr_name) && !f.isRemovable);
        if (!exists) {
          const optsFromBuckets = optionsFromBuckets(master, attr.attr_name, Number(attr.attr_id), selectedDeptId, currentTeamId);
          const baseOpts = optsFromBuckets.length ? optsFromBuckets : normalizeOptions(attr.options);
          const opts = applyCommonFirstOption(baseOpts, attr.attr_name);

          const fromDraft = getDraftValueForAttr(attr);
          const defaultVal =
            typeof fromDraft !== 'undefined' ? fromDraft : defaultValueForAttr(attr.attr_type, attr.multiple_select);

          next.push({
            id: `base-${slugify(attr.attr_name)}-${attr.attr_id}`,
            type: attr.attr_name,
            label: attr.attr_name,
            value: defaultVal,
            isRemovable: false,
            isRequired: Number((attr as any).input_required ?? 0) === 1,
            divisionContext: division,
            options: isSelectType(attr.attr_type) ? uniqueOptions(opts) : undefined,
            attr_id: Number(attr.attr_id)
          });
          added = true;
        } else {
          const optsFromBuckets = optionsFromBuckets(master, attr.attr_name, Number(attr.attr_id), selectedDeptId, currentTeamId);
          const baseOpts = optsFromBuckets.length ? optsFromBuckets : normalizeOptions(attr.options);
          const opts = applyCommonFirstOption(baseOpts, attr.attr_name);
          const attrId = Number(attr.attr_id);
          const isRequired = Number((attr as any).input_required ?? 0) === 1;
          const nextOptions = isSelectType(attr.attr_type) ? uniqueOptions(opts) : undefined;

          next = next.map((f) => {
            if (f.isRemovable) return f;
            if (!sameName(f.type, attr.attr_name)) return f;

            const nextId = String(f.id ?? '').startsWith('base-')
              ? `base-${slugify(attr.attr_name)}-${attr.attr_id}`
              : f.id;
            const optionsChanged = JSON.stringify(f.options ?? null) !== JSON.stringify(nextOptions ?? null);
            const needsUpdate =
              f.attr_id !== attrId ||
              f.type !== attr.attr_name ||
              f.label !== attr.attr_name ||
              f.isRequired !== isRequired ||
              f.divisionContext !== division ||
              f.id !== nextId ||
              optionsChanged;

            if (!needsUpdate) return f;

            return {
              ...f,
              id: nextId,
              type: attr.attr_name,
              label: attr.attr_name,
              isRequired,
              divisionContext: division,
              options: nextOptions,
              attr_id: attrId,
            };
          });

          // ★既に存在している欄で「空」の場合は、後着の draft を適用（入ってる値は潰さない）
          const fromDraft = getDraftValueForAttr(attr);
          if (typeof fromDraft !== 'undefined' && !isBlankValue(fromDraft)) {
            next = next.map((f) => {
              if (f.isRemovable) return f;
              if (!sameName(f.type, attr.attr_name)) return f;
              if (!isBlankValue(f.value)) return f;
              return { ...f, value: fromDraft };
            });
          }
        }
      });

      // 差分が無ければ prev を返す（無限ループ防止）
      const unchanged = !added && next.length === prev.length && next.every((it, i) => it === prev[i]);

      return unchanged ? prev : next;
    });
  }, [activeTab, allDynamicAttrsSorted, master, division, selectedDeptId, currentTeamId, getDraftValueForAttr]);

  /* ---------- freeWord_* の通知（属性名順でグルーピング） ---------- */
  useEffect(() => {
    if (activeTab !== 'new') return; // ★追加（hidden時に親を更新しない）
    if (!isHydrated) return; // ★追加（復元完了まで通知しない）
    if (!onFreeWordsChange) return;

    const dynOrder = allDynamicAttrsSorted.map((a) => a.attr_name);
    const groups = new Map<string, string[]>(); // attr_name -> values[]

    fields.forEach((f) => {
      if (!f || FIXED_BASE_NAMES.has(f.type)) return;
      const val = pickValueLike(f.value);
      if (!val) return;
      const arr = groups.get(f.type) ?? [];
      arr.push(val);
      groups.set(f.type, arr);
    });

    const out: Record<string, string> = {};
    dynOrder.forEach((name, i) => {
      const list = groups.get(name) ?? [];
      if (list.length === 0) return;
      const idx = i + 1;
      out[`freeWord_${idx}`] = list[0];
      list.forEach((v, j) => {
        out[`freeWord_${idx}_${j}`] = v;
      });
    });

    // ★無限レンダリング防止：同一内容は通知しない
    let json = '';
    try {
      json = JSON.stringify(out);
    } catch {
      json = String(Date.now());
    }
    if (prevFreeWordsJsonRef.current === json) return;
    prevFreeWordsJsonRef.current = json;

    onFreeWordsChange(out);
  }, [activeTab, isHydrated, fields, allDynamicAttrsSorted, onFreeWordsChange]);

  /* ---------- バックエンド保存用属性の通知（11/12/13 含む） ---------- */
  useEffect(() => {
    if (activeTab !== 'new') return; // ★追加（hidden時に親を更新しない）
    if (!isHydrated) return; // ★追加（復元完了まで通知しない）
    if (!onAttributesChange) return;

    const meta = new Map<string, { id: number; type: number }>();
    allDynamicAttrsSorted.forEach((a) => meta.set(a.attr_name, { id: Number(a.attr_id), type: toAttrTypeCode(a.attr_type) }));

    const out: Array<{ attr_id: number; attr_name: string; attr_value: string }> = [];
    fields.forEach((f) => {
      const m = meta.get(f.type);
      if (!m) return;
      const val = pickValueLike(f.value);
      if (!val) return;
      out.push({ attr_id: m.id, attr_name: f.type, attr_value: val });
    });

    // ★無限レンダリング防止：同一内容は通知しない
    let json = '';
    try {
      json = JSON.stringify(out);
    } catch {
      json = String(Date.now());
    }
    if (prevAttrsJsonRef.current === json) return;
    prevAttrsJsonRef.current = json;

    onAttributesChange(out);
  }, [activeTab, isHydrated, fields, allDynamicAttrsSorted, onAttributesChange]);

  /* ---------- 表示順 ---------- */

  const fixedOrder: readonly string[] = ['チーム', '記入者', '連名者', '承認-3', '承認AM'] as const;
  const commonOrder = useMemo(() => visibleCommonAttrsSorted.map((a) => a.attr_name), [visibleCommonAttrsSorted]);

  const fixedWithDateOrder = useMemo(() => {
    const idx = commonOrder.indexOf(CLASSIFICATION_MARKET_TARGET_ATTR_NAME);
    return idx >= 0
      ? [...fixedOrder, ...commonOrder.slice(0, idx + 1), '開始終了日付', ...commonOrder.slice(idx + 1)]
      : [...fixedOrder, '開始終了日付', ...commonOrder];
  }, [fixedOrder, commonOrder]);

  const dynamicSelectOrder = useMemo(
    () => uniqueNames(effectiveDynamicAttrsForTeam.filter((a) => isSelectType(a.attr_type)).map((a) => a.attr_name)),
    [effectiveDynamicAttrsForTeam]
  );
  const dynamicFreeOrder = useMemo(() => {
    const freeNames = effectiveDynamicAttrsForTeam
      .filter((a) => isFreeWordType(a.attr_type))
      .map((a) => a.attr_name);

    return uniqueNames(freeNames);
  }, [effectiveDynamicAttrsForTeam]);

  const dynamicDisplayOrder = useMemo(
    () => prioritizeProcessPlantAttr([...dynamicSelectOrder, ...dynamicFreeOrder]),
    [dynamicSelectOrder, dynamicFreeOrder]
  );

  const order = useMemo(() => [...fixedWithDateOrder, ...dynamicDisplayOrder] as const, [
    fixedWithDateOrder,
    dynamicDisplayOrder
  ]);

  const lastCommonAttrName = commonOrder.length > 0 ? commonOrder[commonOrder.length - 1] : null;

  /* ---------- 描画 ---------- */

  return (
    <div>
      <div className="search-areas">
        <div className="search-area-input">
          {Array.from(order).map((type) => {
            // チームは固定＋追加分の2段
            if (type === 'チーム') {
              const added = fields.filter((f) => f.type === 'チーム' && f.isRemovable);
              return (
                <div key="team-group" className="field-group field-group-team">
                  <div className="form-field select-field team">
                    <div className="team-label-row">
                      <label htmlFor="team-static">
                        チーム&nbsp;
                        {SyncIcon}
                      </label>
                      <label className="team-past-toggle">
                        <input
                          type="checkbox"
                          checked={showPastTeams}
                          onChange={(e) => setShowPastTeams(e.target.checked)}
                        />
                        過去チームも表示
                      </label>
                    </div>

                    <CommonSelect
                      id="team-static"
                      placeholder="-- チーム --"
                      options={uniqueOptions(teamOpts)}
                      value={toSelected(team ?? '', uniqueOptions(teamOpts))}
                      onChange={(opt: CSOption | null) => {
                        if (activeTab !== 'new') return;
                        const v = norm(opt?.value ?? '');
                        setTeam?.(v);
                      }}
                    />
                  </div>

                  {added.map((f) => (
                    <div key={f.id} className={`form-field dynamic-field-wrapper add-parent-${clsNameOf('チーム')}`}>
                      {wrapField(f)}
                      {f.isRemovable && (
                        <button
                          className="delete-button"
                          type="button"
                          onClick={() => setFields((p) => p.filter((x) => x.id !== f.id))}
                        />
                      )}
                    </div>
                  ))}
                </div>
              );
            }

            if (type === '記入者') {
              const base = fields.find((f) => f.type === '記入者' && !f.isRemovable);
              const add = fields.filter((f) => f.type === '記入者' && f.isRemovable);
              const baseTask = fields.find((f) => f.type === '連名者' && !f.isRemovable);
              const addTask = fields.filter((f) => f.type === '連名者' && f.isRemovable);

              return (
                <div key="reporter-and-task-group" className="field-group field-group-reporter">
                  {base && wrapField(base)}
                  {add.map(wrapField)}
                  {baseTask && wrapField(baseTask)}
                  {addTask.map(wrapField)}
                </div>
              );
            }

            // 連名者は「記入者」ブロック内で描画しているため、ここでは描画しない
            if (type === '連名者') return null;
            if (type === '課題メンバー') return null;

            if (type === '承認-3') {
              const added = fields.filter((f) => f.type === '承認-3' && f.isRemovable);
              return (
                <div key="approver3-group" className="field-group field-group-approver3">
                  <div className="form-field select-field approver3">
                    <label htmlFor="approver3-static">
                      {APPROVER3_DISPLAY_LABEL}&nbsp;
                      {SyncIcon}
                    </label>

                    <CommonSelect
                      id="approver3-static"
                      placeholder={`-- ${APPROVER3_DISPLAY_LABEL} --`}
                      options={uniqueOptions(approver3Opts)}
                      value={toSelected(approver3 ?? '', uniqueOptions(approver3Opts))}
                      onChange={(opt: CSOption | null) => {
                        if (activeTab !== 'new') return;
                        const v = norm(opt?.value ?? '');
                        setApprover3?.(v);
                      }}
                    />
                  </div>
                  {added.map(wrapField)}
                </div>
              );
            }

            if (type === '承認AM') {
              const amBase = fields.find((f) => f.type === '承認AM' && !f.isRemovable);
              const amAdds = fields.filter((f) => f.type === '承認AM' && f.isRemovable);
              if (!amBase && amAdds.length === 0) return null;
              return (
                <div key="approver-am-group" className="field-group field-group-approver-am">
                  {amBase && wrapField(amBase)}
                  {amAdds.map(wrapField)}
                </div>
              );
            }

            if (type === '開始終了日付') {
              const base = fields.find((f) => f.type === '開始終了日付' && !f.isRemovable);
              return base ? (
                <div key="daterange" className={`field-group field-group-${clsNameOf(String(type))}`}>
                  {wrapField(base)}
                </div>
              ) : null;
            }

            // 動的属性
            const base = fields.find((f) => f.type === type && !f.isRemovable);
            const adds = fields.filter((f) => f.type === type && f.isRemovable);
            if (!base && adds.length === 0) return null;

            const content = (
              <div key={String(type)} className={`field-group field-group-${clsNameOf(String(type))}`}>
                {base && wrapField(base)}
                {adds.map(wrapField)}
              </div>
            );

            if (type === lastCommonAttrName) {
              return (
                <React.Fragment key={`${type}-with-break`}>
                  {content}
                  <div
                    className="row-break-after-common"
                    style={{ gridColumn: '1 / -1', width: '100%', height: 0, margin: 0, padding: 0 }}
                  />
                </React.Fragment>
              );
            }

            return content;
          })}

        </div>
      </div>
    </div>
  );
};

export default SearchArea1st;
